import React from "react";
import { FieldGroupRow, FieldGroupRowStatus } from "../../../models/intake";
import FormConstants from "./DataEntryFieldsConstants";
import { writeFieldToForm } from "./formFieldHelpers";
import DropdownField, { DropdownOption } from "../../../common/formFields/DropdownField";
import { generatePickerOptions } from "../../../common/utils/appUtils";

// DropdownField unconditionally reads props.meta.* when driven outside
// redux-form's <Field> wrapper (same reason RequestTypePurposeField needs
// this) - this component manages its own writes via writeFieldToForm
// instead, so it supplies a static, always-valid meta rather than
// touched/error state.
const STATIC_META = { touched: false, active: false, valid: true, error: undefined };

export interface ClinicResponsibleFieldProps {
    /** Currently selected clinic/department id (read from DATA_ENTRY_FIELDS_FORM). */
    readonly value?: number;
    /** props.transactionDetails.facility.useClinics/useDepartments - drives both visibility and the label wording. */
    readonly useClinics: boolean;
    readonly useDepartments: boolean;
    /** props.discloseInstitutions, options built the same way the classic pane already does via generatePickerOptions. */
    readonly discloseInstitutions?: any;
    readonly dispatch: Function;
    readonly formName?: string;
    /**
     * Extra, non-form side effect the caller wants to run on change (the
     * classic pane's autosave PATCH). The base DATA_ENTRY_FIELDS_FORM write
     * is always handled by this component itself via writeFieldToForm.
     */
    readonly onClinicResponsibleChange?: (newValue: number) => void;
}

/**
 * Shared Clinic/Facility field (Spec #34098 / F1a). Only rendered when the
 * facility actually uses clinics or departments - mirrors the classic
 * pane's own visibility gate exactly (it renders an empty placeholder div
 * instead of nothing there, for its flex layout; the caller decides
 * whether Quick Entry needs an equivalent placeholder or can simply omit
 * the row).
 */
const ClinicResponsibleField: React.FC<ClinicResponsibleFieldProps> = (props) => {
    if (!props.useClinics && !props.useDepartments) {
        return null;
    }

    const formName = props.formName ?? FormConstants.DATA_ENTRY_FIELDS_FORM;
    // generatePickerOptions returns InormalizedType[] ({[key: string]: any}),
    // which is a superset of DropdownOption's shape at runtime but not
    // structurally assignable to it - normalize explicitly.
    const options: readonly DropdownOption[] = generatePickerOptions(props.discloseInstitutions, "name", "id")
        .map(option => ({ label: option.label, value: option.value }));

    const onChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newValue = parseInt(event.target.value, 10);
        writeFieldToForm(props.dispatch, FormConstants.CLINIC_RESPONISBLE, newValue, formName);
        props.onClinicResponsibleChange?.(newValue);
    };

    return (
        <DropdownField
            label={props.useClinics ? "What clinic is responsible for this request?" : "Department responsible for this request:"}
            required
            meta={STATIC_META}
            options={options}
            onChange={onChange}
            value={props.value}
        />
    );
};

export default ClinicResponsibleField;

export const CLINIC_RESPONSIBLE_ROW_ID = "clinicResponsible";

/** Builds the FieldGroupRow for the Clinic/Facility row - always required when visible. */
export function mapClinicResponsibleToFieldGroupRow(
    badgeNumber: number,
    useClinics: boolean,
    formValue: number | undefined,
    discloseInstitutions?: any,
): FieldGroupRow {
    const hasValue = formValue !== undefined && formValue !== null;
    const matched = hasValue ? discloseInstitutions?.[formValue as any] : undefined;

    return {
        id: CLINIC_RESPONSIBLE_ROW_ID,
        badgeNumber,
        status: hasValue ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.NeedsEntry,
        label: useClinics ? "Clinic" : "Department",
        value: hasValue ? (matched?.name ?? String(formValue)) : null,
        isAiPreSelected: false,
    };
}
